import { RootState } from '../store/store';
import { PaymentPeriod, PeriodLicense, PeriodType } from 'src/types/tax';
import { License } from 'src/types/licenses';
import { getAPIManagerInstance } from './apiManager';
import {
  DECKARD_API_URL,
  httpMethodFunctions,
  PaginatedResponse,
} from './common';
import { AxiosResponse } from 'axios';
import { selectCurrentIDToken } from 'src/store/slices/auth/authSelectors';
import { ApplicationData, REGISTRANT_OPTIONS } from 'src/types/flow';
import { encodeApn, sanitizePeople } from './utils';
import { RENTAL_STRUCTURE_STATUS_ENUM } from 'src/common/enums';
import { RequiredHiddable } from 'src/types/configuration';

const ERR_LICENSES = 'Something went wrong while fetching licenses.';

const buildApplicationConfig = (data: ApplicationData) => {
  const {
    registrant,
    property,
    contacts,
    propertyDetails,
    listings,
    certifications,
  } = data;

  let registrantType = registrant?.registrantType ?? '';
  if (registrantType === REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT) {
    registrantType = REGISTRANT_OPTIONS.PROPERTY_MANAGER;
  }

  return {
    apn: property?.propertyid,
    applicationConfig: {
      registrantType,
      unitNumber: property?.unitnumber ?? '',
      misc: {
        supplementalQuestions: { ...propertyDetails?.supplementalQuestions },
        mandatoryAcknowledgements: {
          ...certifications?.mandatoryAcknowledgements,
        },
      },
      people: {
        ...sanitizePeople(registrant),
        ...sanitizePeople(contacts),
      },
      signatory: certifications?.signatory ?? '',
      licenseListings: listings?.map((l: string) => ({ url: l })) ?? [],
    },
  };
};

export const createNewLicense = async (
  data: ApplicationData,
  accessToken: string,
  rentalStructureRequiredStatus: RequiredHiddable
): Promise<{ applicationNumber: string }> => {
  const { apn, applicationConfig } = buildApplicationConfig(data);
  const encodedApn = apn ? encodeApn(apn) : apn;

  const response = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/newLicense/${encodedApn}`,
    {
      ...applicationConfig,
      // only add rentalStructure if it is not set to hidden in the config
      ...(rentalStructureRequiredStatus !==
        RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && {
        rentalStructure: data.property?.rentalstructure ?? null,
      }),
    },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to create apply a license');
};

export const createNewTaxCertificate = async (
  data: ApplicationData,
  state: RootState
): Promise<{ applicationNumber: string }> => {
  const { apn, applicationConfig } = buildApplicationConfig(data);
  const encodedApn = apn ? encodeApn(apn) : apn;
  const accessToken = selectCurrentIDToken(state);
  const rentalStructureRequiredStatus =
    state.config.settings?.express?.registration?.rental_structure;

  const response = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/newTaxCertificate/${encodedApn}`,
    {
      ...applicationConfig,
      // only add rentalStructure if it is not set to hidden in the config
      ...(rentalStructureRequiredStatus !==
        RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && {
        rentalStructure: data.property?.rentalstructure ?? null,
      }),
    },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to create a tax certificate');
};

export const getTaxPaymentPeriodLicenses = async (
  state: RootState,
  period: { startDate: string },
  contToken: string,
  cache: string,
  periodType?: PeriodType
) => {
  const accessToken = selectCurrentIDToken(state);

  const response = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/totPaymentPeriodLicenses`,
    period,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        contToken,
        cache,
        period_type: periodType,
      },
    }
  );
  if (response.status === 200) {
    return {
      contToken: response.data.contToken,
      data: response.data.licenses,
    };
  } else {
    throw new Error('Failed to fetch renewable licenses');
  }
};

export const getTaxableLicenses = async (
  dates: Array<string>,
  state: RootState,
  periodType?: PeriodType
): Promise<Record<string, Array<PeriodLicense>>> => {
  // Get a stable string to user as our cache key
  const now = new Date().getTime().toString();

  // Paginate through all of the licenses for a given payment period date.
  // Collect the results into an array for further use.
  const go = async (
    startDate: string,
    contToken: string
  ): Promise<Array<PeriodLicense>> => {
    const result = await getTaxPaymentPeriodLicenses(
      state,
      { startDate },
      contToken,
      now,
      periodType
    );
    const newContToken: string | null = result.contToken;
    const licenses: Array<PeriodLicense> = result.data;
    // No cont token means we are done.
    if (!newContToken) {
      return licenses;
    } else {
      const nextPageLicenses = await go(startDate, newContToken);
      return [...licenses, ...nextPageLicenses];
    }
  };

  // For each period date that we have been given, collect all of the licenses matching
  // that payment period, returning them in a date-keyed object.
  return dates.reduce(async (prev, date) => {
    return {
      ...prev,
      [date]: await go(date, '0'),
    };
  }, {});
};

export const getTaxablePeriods = async (
  state: RootState
): Promise<Array<PaymentPeriod>> => {
  const accessToken = selectCurrentIDToken(state);
  const result = await httpMethodFunctions.get<Array<PaymentPeriod>>(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/totPaymentPeriodDates`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return result.data;
};

export const getUserRenewableLicenses = async (
  accessToken: string,
  contToken?: number
): Promise<PaginatedResponse<License>> => {
  const url = `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/renewableLicenses`;
  const response = await httpMethodFunctions.get<{
    contToken: number;
    licenses: License[];
  }>(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    ...((contToken || contToken === 0) && {
      params: {
        contToken,
      },
    }),
  });

  if (response.status === 200) {
    return {
      contToken: response.data.contToken,
      data: response.data.licenses,
    };
  } else {
    throw new Error('Failed to fetch renewable licenses');
  }
};

/** Fetches a single page of user licenses from the legacy /userLicenses endpoint. */
const fetchUserLicensesPage = async (
  accessToken: string,
  contToken?: number
): Promise<AxiosResponse<{ contToken: number; licenses: License[] }>> => {
  const url = `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/userLicenses`;

  return httpMethodFunctions.get<{ contToken: number; licenses: License[] }>(
    url,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      ...((contToken || contToken === 0) && {
        params: {
          contToken,
        },
      }),
    }
  );
};

/**
 * Fetches one page of user licenses. Pagination is handled externally — the caller
 * is responsible for following the returned contToken to load subsequent pages.
 */
export const getUserLicenses = async (
  accessToken: string,
  contToken?: number
): Promise<PaginatedResponse<License>> => {
  const response = await fetchUserLicensesPage(accessToken, contToken);
  if (response.status === 200) {
    return {
      contToken: response.data.contToken,
      data: response.data.licenses,
    };
  } else {
    throw new Error('Failed to fetch user licenses');
  }
};

/**
 * Fetches all user licenses across all pages. Pagination is handled internally —
 * the function recurses until no contToken remains and returns the full accumulated result.
 */
export const getAllLicensesForUserAsync = async (
  accessToken: string
): Promise<License[]> => {
  const accumulatedLicenses: License[] = [];

  const fetchLicenses = async (contToken: number) => {
    try {
      const response = await fetchUserLicensesPage(accessToken, contToken);
      const { data } = response;
      const lics = [...accumulatedLicenses, ...data.licenses];

      if (data.contToken) {
        // Recursively call the function if contToken exists
        return fetchLicenses(data.contToken);
      } else {
        // Return accumulated licenses
        return lics;
      }
    } catch (error) {
      throw new Error(ERR_LICENSES);
    }
  };

  return fetchLicenses(0);
};
